import type { Item, ItemStatus, Part, Site } from '../../domain/inventory';
import { operationallyVisibleItems } from './operational-visibility';
import { attentionSourceForItem, type AttentionSource } from './attention-source';

export type CustomerInventoryScope = 'assigned' | 'on-site' | 'away-from-assignment';

export interface InventoryItemFilterValues {
  itemIds?: readonly string[];
  customerId?: string | undefined;
  customerIds?: readonly string[];
  customerScope?: CustomerInventoryScope | null;
  siteId?: string | undefined;
  siteIds?: readonly string[];
  assignedSiteId?: string | undefined;
  locationSiteId?: string | undefined;
  partId?: string | undefined;
  partType?: string | undefined;
  partTypes?: readonly string[];
  statuses?: readonly ItemStatus[];
  attention?: boolean;
  attentionSources?: readonly AttentionSource[];
}

/**
 * Applies the inventory page's semantic filters to its cached fleet snapshot.
 * Keeping this in one place makes every count shown beside a filter option
 * describe the same result set that the option will open.
 */
export function filterInventoryItems(
  items: Item[],
  sites: Site[],
  partsById: ReadonlyMap<string, Part>,
  filters: InventoryItemFilterValues,
): Item[] {
  const sitesById = new Map(sites.map((site) => [site.id, site]));
  let filtered = operationallyVisibleItems(items, sitesById);

  if (filters.itemIds?.length) {
    const itemIds = new Set(filters.itemIds);
    filtered = filtered.filter((item) => itemIds.has(item.id));
  }

  const customerIds = filters.customerIds?.length
    ? filters.customerIds
    : filters.customerId
      ? [filters.customerId]
      : [];
  if (customerIds.length > 0) {
    const customerSiteIds = new Set(
      sites.filter((site) => customerIds.includes(site.customerId)).map((site) => site.id),
    );
    filtered = filtered.filter((item) => {
      const currentSiteId = item.locationSiteId ?? item.siteId;
      if (filters.customerScope === 'assigned') return customerSiteIds.has(item.siteId);
      if (filters.customerScope === 'on-site') return customerSiteIds.has(currentSiteId);
      if (filters.customerScope === 'away-from-assignment') {
        return customerSiteIds.has(item.siteId) && currentSiteId !== item.siteId;
      }
      return customerSiteIds.has(item.siteId) || customerSiteIds.has(currentSiteId);
    });
  }

  const siteIds = filters.siteIds?.length ? filters.siteIds : filters.siteId ? [filters.siteId] : [];
  if (siteIds.length > 0) {
    filtered = filtered.filter(
      (item) =>
        siteIds.includes(item.siteId) || siteIds.includes(item.locationSiteId ?? item.siteId),
    );
  }
  if (filters.assignedSiteId) {
    filtered = filtered.filter((item) => item.siteId === filters.assignedSiteId);
  }
  if (filters.locationSiteId) {
    filtered = filtered.filter(
      (item) => (item.locationSiteId ?? item.siteId) === filters.locationSiteId,
    );
  }
  if (filters.partId) filtered = filtered.filter((item) => item.partId === filters.partId);
  if (filters.statuses && filters.statuses.length > 0) {
    filtered = filtered.filter((item) =>
      filters.statuses?.includes(item.status ?? 'STATUS_UNSPECIFIED'),
    );
  }
  if (filters.attentionSources && filters.attentionSources.length > 0) {
    filtered = filtered.filter((item) => {
      const source = attentionSourceForItem(item);
      return source !== undefined && filters.attentionSources?.includes(source);
    });
  } else if (filters.attention) {
    filtered = filtered.filter((item) => item.needsAttention);
  }
  const partTypes = filters.partTypes?.length
    ? filters.partTypes
    : filters.partType
      ? [filters.partType]
      : [];
  if (partTypes.length > 0) {
    filtered = filtered.filter((item) => partTypes.includes(partsById.get(item.partId)?.type ?? ''));
  }

  return filtered;
}
